/**
 * profiles.timezone defaulted to 'UTC' for years, so a stored 'UTC' (or null)
 * means "never chosen". Edge code falls back to Europe/London for those
 * profiles; the app instead learns the real zone from the browser.
 */
export const FALLBACK_USER_TIME_ZONE = 'Europe/London';

const UNSET_ZONES = new Set(['utc', 'etc/utc', 'gmt', 'etc/gmt', 'z', 'zulu', 'universal', 'etc/universal']);

export function isUnsetTimeZone(zone: string | null | undefined): boolean {
  if (!zone || !zone.trim()) return true;
  return UNSET_ZONES.has(zone.trim().toLowerCase());
}

function isValidTimeZone(zone: string): boolean {
  try {
    new Intl.DateTimeFormat('en-GB', { timeZone: zone });
    return true;
  } catch {
    return false;
  }
}

/** The browser's IANA zone, or null when it is missing, invalid or plain UTC. */
export function browserTimeZone(): string | null {
  try {
    const zone = Intl.DateTimeFormat().resolvedOptions().timeZone;
    return zone && !isUnsetTimeZone(zone) && isValidTimeZone(zone) ? zone : null;
  } catch {
    return null;
  }
}

/** The zone to show times in: the stored one when the user set it, else the browser's, else London. */
export function resolveClientTimeZone(stored: string | null | undefined, browser: string | null = browserTimeZone()): string {
  if (stored && !isUnsetTimeZone(stored) && isValidTimeZone(stored)) return stored;
  return browser ?? FALLBACK_USER_TIME_ZONE;
}

/** The zone to save to the profile on login, or null when the stored one should stay. */
export function timeZoneToBackfill(stored: string | null | undefined, browser: string | null = browserTimeZone()): string | null {
  return isUnsetTimeZone(stored) && browser ? browser : null;
}
